import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Download, Search } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import useDebouncedHandler from '../../../../hooks/useDebouncedHandler';
import AdminLayout from '../../../../layouts/AdminLayout';
import ApiService from '../../../../services/api.service';
import TokenService from '../../../../services/token.service';
import DepartmentFilter from './components/DepartmentFilter';
import ReviewsTable from './components/ReviewsTable';

function AdminReviewsPageV3({ title }) {
  const [reviewsData, setReviewsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'desc' });
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState([]);

  const fetchReviews = useCallback(async () => {
    try {
      setIsLoading(true);
      const sortParam = sortConfig.direction === 'asc' ? sortConfig.key : `-${sortConfig.key}`;

      const response = await ApiService.admin.getReviews({
        params: {
          departmentIds: departmentFilter.join(','),
          search: searchInput,
          skip: currentPage * rowsPerPage,
          limit: rowsPerPage,
          sort: sortParam,
        },
      });

      setReviewsData(response.paginatedResult);
      setTotalCount(response.totalCount ?? 0);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, rowsPerPage, searchInput, sortConfig, departmentFilter]);

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await ApiService.admin.getDepartmentsLvls();
      setDepartments(response);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value.trim());
    setCurrentPage(0);
  };

  const { debouncedHandler } = useDebouncedHandler(handleSearchChange);

  const handleSort = (columnId) => {
    setSortConfig((prev) => ({
      key: columnId,
      direction: prev.key === columnId && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0);
  };

  const handleDepartmentFilterChange = (departmentId) => {
    setDepartmentFilter((prev) => {
      if (prev.includes(departmentId)) {
        return prev.filter((id) => id !== departmentId);
      }
      return [...prev, departmentId];
    });
    setCurrentPage(0);
  };

  const getExportCsvLink = () => {
    const sortParam = sortConfig.direction === 'asc' ? sortConfig.key : `-${sortConfig.key}`;
    const params = {
      departmentIds: departmentFilter.join(','),
      search: searchInput,
      sort: sortParam,
    };

    const query = Object.keys(params)
      .map((key) => `&${key}=${params[key]}`)
      .join('');

    const accessToken = TokenService.getLocalAccessToken();
    return `${process.env.REACT_APP_API_URL}/api/v1/admin/reviews/export?accessToken=${accessToken}${query}`;
  };

  return (
    <AdminLayout title={title}>
      <div className="flex gap-6 p-6">
        {/* Department Filter Sidebar */}
        <DepartmentFilter
          departments={departments}
          selectedDepartments={departmentFilter}
          onFilterChange={handleDepartmentFilterChange}
        />

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Header with Search and Export */}
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search reviews..."
                className="pl-9"
                onChange={debouncedHandler}
              />
            </div>
            <Button variant="outline" size="sm" asChild>
              <a href={getExportCsvLink()} download>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </a>
            </Button>
          </div>

          {/* Reviews Table */}
          <Card>
            <CardContent className="p-0">
              <ReviewsTable
                reviews={reviewsData}
                isLoading={isLoading}
                totalCount={totalCount}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                sortConfig={sortConfig}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}

AdminReviewsPageV3.propTypes = {
  title: PropTypes.string.isRequired,
};

export default AdminReviewsPageV3;
